import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Trash2 } from 'lucide-react';

export interface PhotoDraft {
  file: File;
  preview: string;
  isPrimary: boolean;
}

interface PhotosInputProps {
  value: PhotoDraft[];
  onChange: (photos: PhotoDraft[]) => void;
  radioName?: string;
  onPrimaryChange?: (index: number) => void;
  maxPhotos?: number;
  helpText?: string;
}

export default function PhotosInput({ value, onChange, radioName, onPrimaryChange, maxPhotos, helpText }: PhotosInputProps) {
  const onFilesSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remainingSlots = maxPhotos ? Math.max(0, maxPhotos - value.length) : files.length;
    const drafts: PhotoDraft[] = [];
    Array.from(files).slice(0, remainingSlots).forEach((file, idx) => {
      const preview = URL.createObjectURL(file);
      drafts.push({ file, preview, isPrimary: value.length === 0 && idx === 0 });
    });
    const merged = [...value, ...drafts];
    if (!merged.some((p) => p.isPrimary) && merged.length > 0) merged[0].isPrimary = true;
    onChange(merged);
  };

  const removePhoto = (idx: number) => {
    const draft = value[idx];
    if (draft) URL.revokeObjectURL(draft.preview);
    const photos = value.filter((_, i) => i !== idx);
    if (!photos.some((p) => p.isPrimary) && photos[0]) photos[0].isPrimary = true;
    onChange(photos);
  };

  const setPrimary = (idx: number) => {
    const photos = value.map((p, i) => ({ ...p, isPrimary: i === idx }));
    onChange(photos);
    onPrimaryChange?.(idx);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="photo_files">Upload photos</Label>
        <Input
          id="photo_files"
          type="file"
          accept="image/*"
          multiple
          disabled={maxPhotos != null && value.length >= maxPhotos}
          onChange={(e) => onFilesSelected(e.target.files)}
        />
        <p className="text-xs text-muted-foreground">
          {helpText ?? `Formats: JPG, PNG, WEBP. Max 10MB each, up to ${maxPhotos ?? 10} per upload.`}
        </p>
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {value.map((p, idx) => (
            <Card key={idx} className="p-2">
              <div className="relative">
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <PhotoThumb src={p.preview} alt="Preview" className="w-full h-32 rounded" />
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name={radioName || 'primary_photo'}
                      checked={p.isPrimary}
                      onChange={() => setPrimary(idx)}
                    />
                    <span>Primary</span>
                  </label>
                  <Button type="button" variant="ghost" size="icon" aria-label="Remove" onClick={() => removePhoto(idx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
